import { create } from "zustand";
import { persist } from "zustand/middleware";
import {
  isValidConfiguration,
  ReactFramework,
  TemplateStack,
  CustomDesignSystems,
} from "@/utils/templateProjects";
import type { UploadedFile } from "@/utils/types";

export interface ProjectCreateStore {
  hasHydrated: boolean;
  setHasHydrated: (state) => void;

  generateText: string;
  generateAttachedFiles: UploadedFile[] | null;
  setGenerateText: (text: string) => void;
  setGenerateAttachedFiles: (files: UploadedFile[] | null) => void;

  // Styles builder HTML examples for additional instructions
  stylesBuilderInstructions: string | null;
  setStylesBuilderInstructions: (instructions: string | null) => void;

  // Pending files to upload to codebase once canvas is created
  pendingCodebaseFiles: File[];
  setPendingCodebaseFiles: (files: File[]) => void;
  addPendingCodebaseFiles: (files: File[]) => void;
  clearPendingCodebaseFiles: () => void;

  resetProjectCreateFlow: () => void;
  resetConfiguredOptions: () => void;

  // Project type
  reactFramework: ReactFramework;
  setReactFramework: (framework: ReactFramework) => void;

  templateStackConfigured: boolean;
  setTemplateStackConfigured: (templateStackConfigured: boolean) => void;
  selectedTemplateStack: TemplateStack;
  setSelectedTemplateStack: (stack: TemplateStack | null) => void;

  // FTUE generation tracking
  isFtueGenerating: boolean;
  setIsFtueGenerating: (value: boolean) => void;
}

export const useProjectCreateStore = create<ProjectCreateStore>()(
  persist(
    (set, get) => ({
      hasHydrated: false,
      setHasHydrated: (state) => set({ hasHydrated: state }),

      generateText: "",
      generateAttachedFiles: null,
      setGenerateText: (text: string) => set({ generateText: text }),
      setGenerateAttachedFiles: (files: UploadedFile[] | null) =>
        set({ generateAttachedFiles: files }),

      stylesBuilderInstructions: null,
      setStylesBuilderInstructions: (instructions: string | null) =>
        set({ stylesBuilderInstructions: instructions }),

      pendingCodebaseFiles: [],
      setPendingCodebaseFiles: (files: File[]) =>
        set({ pendingCodebaseFiles: files }),
      addPendingCodebaseFiles: (files: File[]) => {
        const currentFiles = get().pendingCodebaseFiles;
        set({ pendingCodebaseFiles: [...currentFiles, ...files] });
      },
      clearPendingCodebaseFiles: () => set({ pendingCodebaseFiles: [] }),

      resetProjectCreateFlow: () => {
        set({
          generateText: "",
          generateAttachedFiles: null,
          stylesBuilderInstructions: null,
          pendingCodebaseFiles: [],
          reactFramework: ReactFramework.VITE,
          selectedTemplateStack: TemplateStack.BLANK,
          templateStackConfigured: true,
          isFtueGenerating: false,
        });
      },

      resetConfiguredOptions: () => {
        set({
          pendingCodebaseFiles: [],
          reactFramework: ReactFramework.VITE,
          selectedTemplateStack: TemplateStack.BLANK,
          templateStackConfigured: true,
          isFtueGenerating: false,
        });
      },

      reactFramework: ReactFramework.VITE,
      setReactFramework: (reactFramework: ReactFramework) =>
        set((state) => {
          if (
            isValidConfiguration({
              reactFramework,
              selectedTemplateStack: state.selectedTemplateStack,
            })
          ) {
            return { reactFramework };
          }
          if (
            isValidConfiguration({
              reactFramework,
              selectedTemplateStack: TemplateStack.BLANK,
            })
          ) {
            return {
              reactFramework,
              templateStackConfigured: true,
              selectedTemplateStack: TemplateStack.BLANK,
            };
          }
          return {};
        }),

      templateStackConfigured: false,
      setTemplateStackConfigured: (templateStackConfigured: boolean) =>
        set({ templateStackConfigured }),
      selectedTemplateStack: TemplateStack.BLANK,
      setSelectedTemplateStack: (selectedTemplateStack: TemplateStack) =>
        set((state) => {
          const templateStackConfigured = !(
            selectedTemplateStack === TemplateStack.CLERK_CONVEX_STRIPE ||
            selectedTemplateStack === TemplateStack.CLERK_CONVEX_POLAR ||
            selectedTemplateStack === TemplateStack.CLERK_CONVEX ||
            selectedTemplateStack === TemplateStack.SUPABASE_STRIPE ||
            selectedTemplateStack === TemplateStack.SUPABASE_POLAR ||
            selectedTemplateStack === TemplateStack.SUPABASE
          );
          if (
            isValidConfiguration({
              selectedTemplateStack,
              reactFramework: state.reactFramework,
            })
          ) {
            return {
              selectedTemplateStack,
              templateStackConfigured,
            };
          }
          if (
            isValidConfiguration({
              selectedTemplateStack,
              reactFramework: ReactFramework.VITE,
            })
          ) {
            return {
              templateStackConfigured,
              selectedTemplateStack,
              reactFramework: ReactFramework.VITE,
            };
          }
          if (
            isValidConfiguration({
              selectedTemplateStack,
              reactFramework: ReactFramework.NEXT_JS,
            })
          ) {
            return {
              templateStackConfigured,
              selectedTemplateStack,
              reactFramework: ReactFramework.NEXT_JS,
            };
          }
          return {};
        }),

      isFtueGenerating: false,
      setIsFtueGenerating: (value: boolean) => set({ isFtueGenerating: value }),
    }),
    {
      name: "project-create-store",
      onRehydrateStorage: (state) => {
        return () => state.setHasHydrated(true);
      },
      // Note: File objects can't be serialized, so we'll exclude pendingCodebaseFiles from persistence
      // Also exclude runtime state like isFtueGenerating
      partialize: (state) => ({
        ...state,
        pendingCodebaseFiles: [], // Don't persist File objects
        isFtueGenerating: false, // Don't persist runtime state
      }),
    },
  ),
);

export const selectDesignSystem = (state: ProjectCreateStore) => {
  return (
    CustomDesignSystems.find(
      (ds) => ds.templateStack === state.selectedTemplateStack,
    )?.importName ?? null
  );
};
